import {defineStore} from 'pinia'
import api from '@/services/api'
import {useToastsStore} from '@/stores/toasts'
import {useComponentsStore} from '@/stores/components'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'

export const useCategoriesStore = defineStore('categories', {
    state: () => ({
        data: {},
        loadingPromises: {} // e.g. { "tableName.columnName": Promise }
    }),
    
    actions: {
        setData({tableName, columnName, data}) {
            if (!this.data[tableName]) {
                this.data = {
                    ...this.data,
                    [tableName]: {[columnName]: data}
                }
            } else {
                this.data = {
                    ...this.data,
                    [tableName]: {
                        ...this.data[tableName],
                        [columnName]: data
                    }
                }
            }
        },
        
        setLoadingPromise({cacheKey, promise}) {
            this.loadingPromises = {
                ...this.loadingPromises,
                [cacheKey]: promise
            }
        },
        
        removeLoadingPromise(cacheKey) {
            const updatedPromises = {...this.loadingPromises}
            delete updatedPromises[cacheKey]
            this.loadingPromises = updatedPromises
        },
        
        setItemData({tableName, columnName, itemId, data}) {
            if (this.data[tableName] === undefined) {
                this.data[tableName] = {}
            }
            
            if (this.data[tableName][columnName] === undefined) {
                this.data[tableName][columnName] = []
            }
            
            this.data[tableName][columnName] = this.data[tableName][columnName]
                // Remove the item's old category data
                .filter(o => o.itemId !== itemId)
                // Add the new data
                .concat(data)
        },
        
        async refreshComponentData({tableName}) {
            const componentsStore = useComponentsStore()
            const componentStructureStore = useComponentStructureStore()
            
            const componentId = componentsStore.componentIds[tableName]
            
            // Get component structure relationship from componentStructureStore getter/method
            const relationships = componentStructureStore.get(componentId)
                .filter(o => o.type === 'relationshipManyToMany')
            
            await Promise.all(
                relationships.map(({columnName}) =>
                    this.getTableColumnData({tableName, columnName, refresh: true})
                )
            )
        },
        
        async getTableColumnData({tableName, columnName, refresh}) {
            const cacheKey = `${tableName}.${columnName}`
            
            // 1. If we already have the data and aren't forcing a refresh, return it immediately
            if (this.data?.[tableName]?.[columnName] !== undefined && !refresh) {
                return this.data[tableName][columnName]
            }
            
            // 2. If a request for this specific data is already in progress, return that ongoing promise
            if (this.loadingPromises[cacheKey] && !refresh) {
                return this.loadingPromises[cacheKey]
            }
            
            // 3. Create the promise for the API call
            const requestPromise = (async () => {
                try {
                    const {data} = await api.get(`api/categories/get-table-column-data/${tableName}/${columnName}`)
                    
                    this.setData({tableName, columnName, data})
                    return data
                } finally {
                    // Clean up the loading promise tracking once finished/failed
                    this.removeLoadingPromise(cacheKey)
                }
            })()
            
            // Store the promise in the state so later components can await it
            this.setLoadingPromise({cacheKey, promise: requestPromise})
            
            return requestPromise
        },
        
        async enableCategories({componentId}) {
            const toastsStore = useToastsStore()
            const componentsStore = useComponentsStore()
            const componentStructureStore = useComponentStructureStore()
            const itemDataStore = useItemDataStore()
            
            const {tableName, title, roles, rolesGroup} = componentsStore.items.find(o => o.id === componentId)
            const categoryTitle = title + ' categories'
            const categoryTableName = tableName + '_categories'
            
            // Validate - The "<tableName>_categories" table cannot exist
            if (componentsStore.componentIds[categoryTableName] !== undefined) {
                toastsStore.add({
                    body: `
            <p>The table <strong>${categoryTableName}</strong> exists!</p>
            <p class="mb-0">Unable to create the category component.</p>
          `,
                    colourScheme: 'danger',
                    disableAutoHide: true
                })
                return
            }
            
            // Validate - The "category" field cannot exist
            const fieldName = 'category'
            const fields = componentStructureStore.get(componentId)
            if (fields.find(o => o.columnName === fieldName)) {
                toastsStore.add({
                    body: `
            <p>The field <strong>${fieldName}</strong> exists!</p>
            <p class="mb-0">Unable to create the category component.</p>
          `,
                    colourScheme: 'danger',
                    disableAutoHide: true
                })
                return
            }
            
            // Get the fieldset ID for the new category field.
            const response = await api.get('api/component/fieldset/id', {
                params: {componentId, sort: 'displayOrder'}
            })
            
            const firstFieldsetId = response.data.values[0]
            
            // Create the new categories component via Pinia action
            const {data: {id: newComponentId}} = await itemDataStore.insert({
                tableName: 'modulecomponents',
                data: {
                    title: categoryTitle,
                    tableName: categoryTableName,
                    showDisplayOrder: 1,
                    addNew: 1,
                    updateOption: 1,
                    deleteOption: 1,
                    showMetadata: 1,
                    useArchive: 1,
                    enableCache: 1,
                    roles,
                    rolesGroup
                }
            })
            
            const {data: {id: categoryFieldId}} = await itemDataStore.insert({
                tableName: 'modulestructure',
                data: {
                    modulecomponentsID: newComponentId, // todo Deprecated
                    modulecomponentsId: newComponentId,
                    fieldsetId: firstFieldsetId,
                    categoryComponentId: newComponentId,
                    title: 'Category',
                    columnName: 'category',
                    type: 'relationshipManyToMany'
                }
            })
            
            await itemDataStore.update({
                tableName: 'modulecomponents',
                id: componentId,
                data: {
                    categoryColumn: categoryFieldId,
                    categoryComponent: newComponentId,
                    categoryList: 1
                }
            })
            
            const {data: {id: fieldsetId}} = await itemDataStore.insert({
                tableName: 'fieldset',
                data: {
                    componentId: newComponentId,
                    legend: 'General'
                }
            })
            
            // Create the new category component's Title field.
            await itemDataStore.insert({
                tableName: 'modulestructure',
                data: {
                    modulecomponentsID: newComponentId, // todo Deprecated
                    modulecomponentsId: newComponentId,
                    fieldsetId,
                    title: 'Title',
                    columnName: 'title',
                    type: 'text',
                    browser: 1
                }
            })
            
            // Create the new category component's Parent category field.
            await itemDataStore.insert({
                tableName: 'modulestructure',
                data: {
                    modulecomponentsID: newComponentId, // todo Deprecated
                    modulecomponentsId: newComponentId,
                    fieldsetId,
                    title: 'Parent category',
                    columnName: 'parentId',
                    type: 'relationshipOneToMany',
                    categoryComponentId: newComponentId,
                    browser: 1
                }
            })
            
            // Create the new category component's Hide field.
            await itemDataStore.insert({
                tableName: 'modulestructure',
                data: {
                    modulecomponentsID: newComponentId, // todo Deprecated
                    modulecomponentsId: newComponentId,
                    fieldsetId,
                    title: 'Hide',
                    columnName: 'hide',
                    type: 'checkbox',
                    browser: 1
                }
            })
            
            return newComponentId
        },
        
        async disableCategories({componentId}) {
            const componentsStore = useComponentsStore()
            const componentStructureStore = useComponentStructureStore()
            const itemDataStore = useItemDataStore()
            
            const {categoryColumn, categoryComponent} = componentsStore.items.find(o => o.id === componentId)
            
            // Unset the category specific fields in the component.
            await itemDataStore.update({
                tableName: 'modulecomponents',
                id: componentId,
                data: {
                    categoryColumn: 0,
                    categoryComponent: 0,
                    categoryList: 0
                }
            })
            
            // Delete the category column. E.g. Services -> categories
            if (categoryColumn) {
                await itemDataStore.delete({
                    tableName: 'modulestructure',
                    id: categoryColumn
                })
                
                await componentStructureStore.init()
            }
            
            // Delete the category component. E.g. Services categories
            await itemDataStore.delete({
                tableName: 'modulecomponents',
                id: categoryComponent
            })
            
            // Trigger components re-init directly via Pinia action
            await componentsStore.init()
        }
    }
})